import { Alert, Button, Card, Group, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { useCallback, useEffect, useState } from 'react'
import { IconInfoCircle } from '@tabler/icons-react'
import { convertFromISODate } from '@/utils/coverageUtils'
import { formatDateTime } from '@/utils'
import { downloadAttachmentFile } from '@/utils/downloadAttachment'
import {
  closePatientInsuranceRequest,
  resolvePatientInsuranceRequestImageUrls,
  type PatientInsuranceRequest,
} from '@/utils/insuranceQuestionnaire'
import { ImageZoomModal } from './ImageZoomModal'

type InsurancePhotoSide = 'front' | 'back'
type InsurancePhotoItem = { key: InsurancePhotoSide; label: string; url: string | null }
type DownloadableInsurancePhotoItem = InsurancePhotoItem & { url: string }
type ZoomedInsurancePhoto = DownloadableInsurancePhotoItem | null

type Props = {
  request: PatientInsuranceRequest | null
  /**
   * The stored request changed: closed here, closed elsewhere, or changed by the patient since it
   * loaded. The parent re-reads it, which is what puts the patient's newer answers on screen.
   */
  onDismissed?: () => void
  tone?: 'default' | 'compact'
}

function buildInsuranceCardDownloadFileName(payerName: string, side: InsurancePhotoSide): string {
  const payerSlug = payerName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `${payerSlug || 'insurance'}-${side}-card`
}

export function PatientInsuranceRequestPanel({ request, onDismissed, tone = 'default' }: Props) {
  const medplum = useMedplum()
  const [loading, setLoading] = useState(false)
  const [downloadingSide, setDownloadingSide] = useState<InsurancePhotoSide | null>(null)
  const [zoomedPhoto, setZoomedPhoto] = useState<ZoomedInsurancePhoto>(null)
  const [imageUrls, setImageUrls] = useState<Record<InsurancePhotoSide, string | null>>({
    front: null,
    back: null,
  })

  const loadImages = useCallback(async () => {
    if (!request) {
      setImageUrls({ front: null, back: null })
      return
    }

    try {
      setImageUrls(await resolvePatientInsuranceRequestImageUrls(medplum, request))
    } catch {
      setImageUrls({ front: null, back: null })
    }
  }, [medplum, request])

  useEffect(() => {
    void loadImages()
  }, [loadImages])

  if (!request) {
    return null
  }

  const handleDismiss = async () => {
    setLoading(true)
    try {
      const result = await closePatientInsuranceRequest(medplum, request)
      onDismissed?.()
      if (result.outcome === 'changed') {
        showNotification({
          color: 'red',
          message: result.current
            ? 'The patient changed this request after you opened it. Review the updated request, then close it.'
            : 'This request changed after you opened it. Review the current request, then close it.',
        })
      } else {
        showNotification({
          color: 'green',
          message:
            result.outcome === 'closed'
              ? 'Patient insurance request dismissed.'
              : 'This request was already closed.',
        })
      }
    } catch (error) {
      showNotification({
        color: 'red',
        message: (error as Error).message || 'Unable to dismiss patient insurance request.',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadPhoto = async (side: InsurancePhotoSide, url: string) => {
    setDownloadingSide(side)

    try {
      await downloadAttachmentFile(
        medplum,
        url,
        buildInsuranceCardDownloadFileName(request.payerName, side),
        { fallbackExtension: 'jpg' },
      )
    } catch (error) {
      showNotification({
        color: 'red',
        message: (error as Error).message || `Unable to download ${side} insurance photo.`,
      })
    } finally {
      setDownloadingSide((currentSide) => (currentSide === side ? null : currentSide))
    }
  }

  const allPhotoItems: InsurancePhotoItem[] = [
    { key: 'front', label: 'Front', url: imageUrls.front },
    { key: 'back', label: 'Back', url: imageUrls.back },
  ]
  const photoItems = allPhotoItems.filter((item): item is DownloadableInsurancePhotoItem =>
    Boolean(item.url),
  )

  return (
    <>
      <ImageZoomModal
        opened={Boolean(zoomedPhoto)}
        imageUrl={zoomedPhoto?.url ?? null}
        title={`${zoomedPhoto?.label ?? 'Insurance'} Photo`}
        subtitle={`${request.payerName} insurance card`}
        imageAlt="Insurance card"
        downloadLabel={`Download ${zoomedPhoto?.label ?? 'Insurance'} Photo`}
        downloadLoading={Boolean(zoomedPhoto && downloadingSide === zoomedPhoto.key)}
        downloadDisabled={Boolean(
          zoomedPhoto && downloadingSide && downloadingSide !== zoomedPhoto.key,
        )}
        onDownload={
          zoomedPhoto ? () => void handleDownloadPhoto(zoomedPhoto.key, zoomedPhoto.url) : undefined
        }
        onClose={() => setZoomedPhoto(null)}
      />
      <div
        className={
          tone === 'compact'
            ? 'rounded-lg border border-amber-200 bg-amber-50 p-3'
            : 'rounded-lg border border-amber-200 bg-amber-50 p-4'
        }
      >
        <Group justify="space-between" align="flex-start" mb="xs">
          <div>
            <Text fw={600} c="orange.9">
              {request.fromZocdoc ? 'Zocdoc Submission' : 'Patient Submission'}
            </Text>
          </div>
          <Button
            size="xs"
            variant="outline"
            color="gray"
            loading={loading}
            onClick={handleDismiss}
          >
            Cancel Request
          </Button>
        </Group>

        <Stack gap={4}>
          <Text size="sm">
            <b>Payer:</b> {request.payerName}
          </Text>
          <Text size="sm">
            <b>Coverage type:</b> {request.type}
          </Text>
          <Text size="sm">
            <b>Member ID:</b> {request.insuranceNumber || '-'}
          </Text>
          {request.groupNumber && (
            <Text size="sm">
              <b>Group:</b> {request.groupNumber}
            </Text>
          )}
          <Text size="sm">
            <b>Submitted:</b>{' '}
            {request.authored ? formatDateTime(request.authored, '-', true, true) : '-'}
          </Text>
          {request.subscriberName && (
            <Text size="sm">
              <b>Subscriber:</b> {request.subscriberName}
            </Text>
          )}
          {request.subscriberDob && (
            <Text size="sm">
              <b>Subscriber DOB:</b> {convertFromISODate(request.subscriberDob)}
            </Text>
          )}
          {request.subscriberMemberId && (
            <Text size="sm">
              <b>Subscriber Member ID:</b> {request.subscriberMemberId}
            </Text>
          )}
        </Stack>

        {imageUrls.front || imageUrls.back ? (
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            {photoItems.map((item) => (
              <Card key={item.key} withBorder padding="sm" radius="md" className="bg-white">
                <Stack gap="xs">
                  <Text size="sm" fw={600}>
                    {item.label} Photo
                  </Text>
                  <button
                    type="button"
                    className="block w-full cursor-pointer border-0 bg-transparent p-0"
                    aria-label={`View ${item.label.toLowerCase()} insurance card`}
                    onClick={() => setZoomedPhoto(item)}
                  >
                    <div className="flex h-44 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50 p-2">
                      <img
                        src={item.url}
                        alt={`${item.label} insurance card`}
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  </button>
                  <Button
                    className="self-start px-0"
                    variant="subtle"
                    size="xs"
                    loading={downloadingSide === item.key}
                    disabled={Boolean(downloadingSide && downloadingSide !== item.key)}
                    onClick={() => void handleDownloadPhoto(item.key, item.url)}
                  >
                    Download {item.label} Photo
                  </Button>
                </Stack>
              </Card>
            ))}
          </div>
        ) : (
          <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light" mt="sm">
            No insurance card photos were attached to this request.
          </Alert>
        )}
      </div>
    </>
  )
}
